import { useNavigate } from "react-router-dom";
import { useMemo, useState } from "react";
import { usePeriodicLoadData } from "../hooks/usePeriodicLoadData";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/common/Card";
import { Badge } from "../components/common/Badge";
import Button from "../components/common/Button";
import { Calendar } from "../components/common/Calendar";
import * as utils from "../utils/utils";
import { useAuth } from "../lib/AuthContext";
import { useData } from "../lib/DataContext";
import { getLocalizedDayName } from "../i18n/scheduleLabels";
import {
  generateTimeSlots,
  formatDate,
  formatDateDMY,
  formatClockTimeRange,
  getActiveSchedulesForDate,
  getScheduleConsultationMode,
} from "../lib/ScheduleUtils";
import { ConsultationModeBadge } from "./ConsultationMode";
import useBrandTranslation from "../hooks/useBrandTranslation";
import {
  CalendarDays,
  Clock,
  User,
  Share2,
  Plus,
  ArrowLeft,
} from "lucide-react";

export default function SecretarySharedSchedules() {
  const { t } = useBrandTranslation();
  const { user } = useAuth();
  console.log(user);
  const {
    schedules,
    appointments,
    profiles,
    getSharedSchedulesBySecretary,
    loadData,
  } = useData();
  const navigate = useNavigate();

  usePeriodicLoadData(
    loadData,
    undefined,
    ["secretary", "institution"].includes(user?.role)
  );
  const [selectedDate, setSelectedDate] = useState(new Date());

  const mySharedSchedules = ["secretary", "institution"].includes(user?.role)
    ? getSharedSchedulesBySecretary(user.id)
    : [];

  // Obtener los horarios completos
  const availableSchedules = useMemo(() => {
    return mySharedSchedules
      .map((shared) => schedules.find((s) => s.id === shared.schedule_id))
      .filter(Boolean);
  }, [mySharedSchedules, schedules]);

  // Obtener slots para la fecha seleccionada
  const daySchedule = useMemo(() => {
    const dayOfWeek = selectedDate.getDay();
    const dateStr = formatDate(selectedDate);

    const daySchedules = availableSchedules.filter(
      (s) =>
        s &&
        getActiveSchedulesForDate([s], s.doctor_id, selectedDate).length > 0
    );
    if (daySchedules.length === 0) return null;

    const allSlots = daySchedules.flatMap((schedule) => {
      if (!schedule) return [];
      const doctorAppointments = appointments.filter((a) => {
        const appointmentDate = (a.date || "").substring(0, 10);
        return (
          appointmentDate === dateStr &&
          String(a.doctor_id) === String(schedule.doctor_id)
        );
      });
      return generateTimeSlots(
        schedule.start_time,
        schedule.end_time,
        schedule.slot_duration,
        schedule.appointment_type,
        doctorAppointments,
        getScheduleConsultationMode(schedule)
      ).map((slot) => ({
        ...slot,
        doctorId: schedule.doctor_id,
        scheduleId: schedule.id,
      }));
    });

    return {
      date: selectedDate,
      dayOfWeek,
      slots: allSlots.sort((a, b) => a.startTime.localeCompare(b.startTime)),
    };
  }, [selectedDate, availableSchedules, appointments]);

  // Agrupar horarios por médico y día (API/DB usan snake_case)
  const groupedSchedules = useMemo(() => {
    const grouped = {};

    mySharedSchedules.forEach((shared) => {
      const schedule = schedules.find((s) => s.id === shared.schedule_id);
      if (schedule) {
        const key = `${schedule.doctor_id}-${schedule.day_of_week}`;
        if (!grouped[key]) {
          grouped[key] = [];
        }
        grouped[key].push({
          ...schedule,
          sharedAt: shared.created_at ? new Date(shared.created_at) : null,
        });
      }
    });

    return grouped;
  }, [mySharedSchedules, schedules]);

  const getDoctorName = (doctorId) => {
    if (!doctorId) return "Médico";
    const profile = profiles?.find((p) => p.id === doctorId);
    if (profile) return `Dr. ${profile.name || ""} ${profile.last_name || ""}`.trim() || "Médico";
    return "Médico";
  };

  const handleQuickAssign = (slot) => {
    const dateParam = formatDate(selectedDate);
    const doctorParam = slot.doctorId
      ? `&doctorId=${encodeURIComponent(slot.doctorId)}`
      : "";
    navigate(
      `/secretary/appointments?date=${dateParam}&time=${slot.startTime}${doctorParam}`
    );
  };

  // Función para determinar si una fecha debe estar deshabilitada
  const isDateDisabled = (date) => {
    const today = new Date();

    // Normalizar fechas para comparar solo día, mes y año
    const normalizeDate = (d) =>
      new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const normalizedDate = normalizeDate(date);
    const normalizedToday = normalizeDate(today);

    return normalizedDate < normalizedToday;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="outline"
              onClick={() => utils.goBack()}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />{t("common.backHome", "Volver al Inicio")}</Button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Horarios Compartidos
          </h1>
          <p className="text-gray-600">
            Ve los horarios que los médicos han compartido contigo
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendario */}
          <Card>
            <CardHeader>
              <CardTitle>{t("app.appointments.selectDate", "Seleccionar Fecha")}</CardTitle>
            </CardHeader>
            <CardContent>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => date && setSelectedDate(date)}
                disabled={isDateDisabled}
                className="rounded-md border"
              />
            </CardContent>
          </Card>

          {/* Horarios del día seleccionado */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarDays className="w-5 h-5" />
                {getLocalizedDayName(selectedDate.getDay())}{" "}
                {formatDateDMY(selectedDate)}
              </CardTitle>
            </CardHeader>
            <CardContent className="!p-2">
              {!daySchedule || daySchedule.slots.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No tienes horarios compartidos disponibles para este día
                </p>
              ) : (
                <div className="space-y-1 md:space-y-2 max-h-96 overflow-y-auto">
                  {daySchedule.slots.map((slot, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-1.5 md:p-3 rounded-lg border gap-1.5 md:gap-2 ${
                        slot.isAvailable
                          ? "bg-green-50 border-green-200"
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <div className="flex items-center gap-1.5 md:gap-3 flex-1 min-w-0">
                        <Clock className="w-3 h-3 md:w-4 md:h-4 text-gray-500 flex-shrink-0" />
                        <span className="font-medium text-xs md:text-base whitespace-nowrap">
                          {formatClockTimeRange(slot.startTime, slot.endTime)}
                        </span>
                        <Badge
                          variant={
                            slot.appointmentType === "private"
                              ? "appointmentPrivate"
                              : "appointmentInsurance"
                          }
                          className="text-xs flex-shrink-0"
                        >
                          {slot.appointmentType === "private"
                            ? "Particular"
                            : "Obra Social"}
                        </Badge>
                        <ConsultationModeBadge
                          mode={
                            slot.appointment?.consultation_mode ||
                            slot.consultationMode
                          }
                          className="text-xs flex-shrink-0"
                        />
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        {slot.isAvailable ? (
                          <>
                            <Badge
                              variant="outline"
                              className="bg-green-100 text-green-800 text-xs hidden sm:inline-flex"
                            >
                              Disponible
                            </Badge>
                            <Button
                              size="sm"
                              onClick={() => handleQuickAssign(slot)}
                              className="text-xs px-2 h-7 md:h-auto md:px-3 py-1 flex items-center gap-1"
                            >
                              <Plus className="w-3 h-3 md:mr-1" />
                              <span className="hidden md:inline">{t("app.secretary.assign", "Asignar")}</span>
                            </Button>
                          </>
                        ) : (
                          <>
                            <Badge variant="secondary" className="text-xs">
                              Ocupado
                            </Badge>
                            <Badge
                              variant="sobreturno"
                              className="text-xs hidden sm:inline-flex"
                            >
                              Sobreturno
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              title={t("app.secretary.assignOverbookingTitle", "Asignar sobreturno en este horario")}
                              onClick={() => handleQuickAssign(slot)}
                              className="text-xs px-2 h-7 md:h-auto md:px-3 py-1 flex items-center gap-1 border-orange-300 text-orange-900 bg-orange-50/90 hover:bg-orange-100"
                            >
                              <Plus className="w-3 h-3 md:mr-1" />
                              <span className="hidden md:inline">{t("app.secretary.overbooking", "Sobreturno")}</span>
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Lista de todos los horarios compartidos */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5" />
              Todos los Horarios Compartidos
            </CardTitle>
          </CardHeader>
          <CardContent>
            {Object.keys(groupedSchedules).length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                No tienes horarios compartidos aún. Los médicos pueden compartir
                sus horarios contigo.
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(groupedSchedules).map(
                  ([key, scheduleGroup]) => {
                    // key es "uuid-doctor-day_of_week"; el doctor_id es UUID (tiene guiones), el día es el último segmento
                    const parts = key.split("-");
                    const dayOfWeekStr = parts.pop();
                    const doctorId = parts.join("-");
                    const doctorName = getDoctorName(doctorId);
                    const dayIndex = Number.parseInt(dayOfWeekStr, 10);
                    const dayName = Number.isNaN(dayIndex) ? "" : getLocalizedDayName(dayIndex);

                    return (
                      <div key={key} className="border rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-3">
                          <User className="w-4 h-4 text-gray-500" />
                          <span className="font-medium">{doctorName}</span>
                          <Badge variant="secondary">{dayName}</Badge>
                        </div>

                        <div className="space-y-2">
                          {scheduleGroup.map((schedule) => (
                            <div
                              key={schedule.id}
                              className="bg-gray-50 p-2 rounded text-sm"
                            >
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-2">
                                  <Clock className="w-3 h-3 text-gray-500" />
                                  <span>
                                    {formatClockTimeRange(schedule.start_time, schedule.end_time)}
                                  </span>
                                  <Badge
                                    variant={
                                      schedule.appointment_type === "private"
                                        ? "appointmentPrivate"
                                        : "appointmentInsurance"
                                    }
                                    className="text-xs"
                                  >
                                    {schedule.appointment_type === "private"
                                      ? "Particular"
                                      : "Obra Social"}
                                  </Badge>
                                  <ConsultationModeBadge
                                    mode={getScheduleConsultationMode(schedule)}
                                    className="text-xs"
                                  />
                                </div>
                              </div>
                              <div className="text-xs text-gray-500 mt-1">
                                Turnos de {schedule.slot_duration} min
                                {schedule.sharedAt && (
                                  <>
                                    {" • "}
                                    Compartido el{" "}
                                    {formatDateDMY(schedule.sharedAt)}
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Información para la secretaria */}
        <Card className="mt-6 mb-20">
          <CardHeader>
            <CardTitle>{t("app.commonUi.importantInfo", "Información Importante")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <h4 className="font-medium">{t("app.secretary.whatYouCanDo", "¿Qué puedes hacer?")}</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>
                    {t(
                      "app.secretary.viewSharedSchedules",
                      "Ver los horarios que los médicos han compartido contigo"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.secretary.assignFromSlots",
                      "Asignar turnos desde slots disponibles o sobreturnos en horarios ya ocupados"
                    )}
                  </li>
                  <li>{t("app.secretary.viewRealtimeAvailability", "Ver la disponibilidad en tiempo real")}</li>
                  <li>{t("app.secretary.manageAppointments", "Gestionar turnos para pacientes")}</li>
                </ul>
              </div>
              <div className="space-y-2">
                <h4 className="font-medium">{t("app.secretary.limitations", "Limitaciones")}</h4>
                <ul className="list-disc list-inside space-y-1 text-gray-600">
                  <li>
                    {t(
                      "app.secretary.onlySharedWithYou",
                      "Solo puedes ver horarios compartidos específicamente contigo"
                    )}
                  </li>
                  <li>
                    {t(
                      "app.secretary.cannotSeePrivate",
                      "No puedes ver turnos particulares o de obra social existentes"
                    )}
                  </li>
                  <li>{t("app.secretary.cannotModifySchedules", "No puedes modificar los horarios del médico")}</li>
                  <li>
                    {t(
                      "app.secretary.overbookingHint",
                      "En turnos ocupados podés ir a asignar un sobreturno desde el botón correspondiente"
                    )}
                  </li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
